import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Gift, Loader2, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Section } from '@/components/common/Blocks'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { confirmModal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDate, fmtMoney } from '../../../shared/format'
import { toastError, useCrm } from '../../ui'

type GiftRow = { id: string; item: string; value: string; givenAt: string; note: string; givenBy: string; createdBy: string }

const key = (id: string) => ['crm', 'gifts', id] as const

/** Müşteriye verilen hediyeler. Kaydı yalnız giren kişi ya da crm:note:manage yetkilisi silebilir. */
export function GiftsCard({ customerId, canWrite }: { customerId: string; canWrite: boolean }) {
  const { t } = useTranslation()
  const { can, user } = useAuth()
  const { staffName } = useCrm()
  const qc = useQueryClient()
  const gifts = useQuery({
    queryKey: key(customerId),
    queryFn: async () =>
      asList(await api.get<Record<string, unknown>[]>(`/api/v1/accounts/${customerId}/gifts`)).map(
        (r): GiftRow => ({
          id: String(r.id ?? ''),
          item: String(r.item ?? ''),
          value: Number(r.value ?? 0).toFixed(2),
          givenAt: String(r.given_at ?? ''),
          note: String(r.note ?? ''),
          givenBy: String(r.given_by ?? ''),
          createdBy: String(r.created_by ?? ''),
        }),
      ),
  })
  const onSuccess = () => qc.invalidateQueries({ queryKey: key(customerId) })
  const add = useMutation({ mutationFn: (b: { item: string; value: number; given_at: string; note: string }) => api.post(`/api/v1/accounts/${customerId}/gifts`, b), onSuccess })
  const remove = useMutation({ mutationFn: (id: string) => api.del(`/api/v1/accounts/${customerId}/gifts/${id}`), onSuccess })
  const [draft, setDraft] = useState<{ item: string; value: string; date: string; note: string } | null>(null)
  const canDelete = (g: GiftRow) => canWrite && (can('crm:note:manage') || g.createdBy === user?.id)

  const onSave = () => {
    if (!draft || draft.item.trim().length < 2) return
    add.mutate(
      { item: draft.item.trim(), value: Number(draft.value) || 0, given_at: draft.date, note: draft.note.trim() },
      {
        onSuccess: () => {
          setDraft(null)
          toast.success(t('crm.gifts.saved'))
        },
        onError: toastError,
      },
    )
  }
  const onDelete = async (g: GiftRow) => {
    if (!(await confirmModal({ title: t('crm.gifts.deleteTitle'), description: g.item, confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate(g.id, { onSuccess: () => toast.success(t('common.deleted')), onError: toastError })
  }

  return (
    <Section
      title={t('crm.gifts.title')}
      icon={Gift}
      bodyClassName="p-0"
      actions={
        canWrite && !draft ? (
          <Button size="sm" variant="ghost" onClick={() => setDraft({ item: '', value: '', date: new Date().toISOString().slice(0, 10), note: '' })}>
            <Plus />
            {t('crm.gifts.add')}
          </Button>
        ) : null
      }
    >
      {draft ? (
        <div className="space-y-2 border-b p-4">
          <Input aria-label={t('crm.gifts.item')} placeholder={t('crm.gifts.item')} value={draft.item} maxLength={255} onChange={(e) => setDraft({ ...draft, item: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <NumberInput aria-label={t('crm.gifts.value')} value={draft.value} onChange={(v) => setDraft({ ...draft, value: v })} suffix="₺" minDecimalsOnBlur={2} />
            <DateField aria-label={t('crm.gifts.date')} value={draft.date} onChange={(v) => setDraft({ ...draft, date: v })} />
          </div>
          <Input aria-label={t('crm.gifts.note')} placeholder={t('crm.gifts.note')} value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" onClick={onSave} disabled={add.isPending || draft.item.trim().length < 2}>
              {add.isPending ? <Loader2 className="animate-spin" /> : null}
              {t('common.save')}
            </Button>
          </div>
        </div>
      ) : null}
      {!gifts.data?.length ? (
        <p className="p-6 text-center text-sm text-muted-foreground">{t('crm.gifts.empty')}</p>
      ) : (
        <ul className="divide-y">
          {gifts.data.map((g) => (
            <li key={g.id} className="flex items-start gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{g.item}</p>
                <p className="text-xs text-muted-foreground">
                  {g.givenAt ? fmtDate(g.givenAt) : '—'} · {staffName(g.givenBy)}
                  {g.note ? ` · ${g.note}` : ''}
                </p>
              </div>
              {Number(g.value) > 0 ? <span className="text-sm font-semibold tabular-nums">{fmtMoney(g.value)}</span> : null}
              {canDelete(g) ? (
                <Button size="icon" variant="ghost" className="size-8" aria-label={t('common.delete')} onClick={() => void onDelete(g)}>
                  <Trash2 />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}
